/**
 * The "Apple" PDP — a single rail of decisions, with the album preview shrinking
 * by sticky (mobile) rather than by zoom. Its cart property contract matches the
 * Preact configurator (`pdp-configurator.js`), so that
 * `snippets/cart-drawer-items.liquid` renders the lines unchanged.
 *
 * Music search runs against Spotify, through the same client-credentials
 * client the classic PDP uses (pdp-spotify-search.js, extracted from
 * pdp-configurator.js). It has to: the card sells "um QR do Spotify" and
 * production generates the QR from the `_spotify_url` this search records —
 * the iTunes Search API that stood in here was writing music.apple.com URLs
 * into orders (QA 24/08).
 */

import { resolveInstallments } from './pdp-installments.js';
import { glideTo, prefersReducedMotion } from './pdp-apple-glide.js';
import { resizeViewportWithKeyboard } from './pdp-apple-viewport.js';
// The group-replace engine (add first, track by _group, resolve keys after the
// add) lives in the old porta-retrato module, where it was extracted first —
// the items builder is injected, so the lines stay this page's own. It is what
// makes the drawer's ?edit= link reopen this page with the whole state and
// save without duplicating (QA 24/08).
import { saveGroup } from './pdp-porta-retrato.js';
import { createSpotifySearch } from './pdp-spotify-search.js';

const money = (cents) =>
  'R$ ' + (cents / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const isMobile = () => matchMedia('(max-width:749.99px)').matches;

// The timings of the "personalize before buying" entrance. The delay is there so
// the preview appears before the page moves: it is what makes her understand that
// what showed up was the preview, and not the page jumping on its own.
const REVEAL_MS = 450;
const GLIDE_DELAY = 120;
const GLIDE_MS = 1050;
const COLORS_GAP = 10;

const newGroupId = () => `g_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// The same rule as the tested configurator (pdp-configurator.js): a Latin
// whitelist plus an emoji strip, with NFC first so maxlength counts what the
// engraver counts.
const DISALLOWED = /[^A-Za-z0-9\s.,?:;'"~`^@|()&/\-´˜ˆ¨°À-ÿ̀-ͯ]/gu;
const sanitizeText = (raw) => (raw || '').normalize('NFC').replace(/\p{Extended_Pictographic}/gu, '').replace(DISALLOWED, '');

const SIZE_ORDER = ['P', 'M', 'G'];

// The engraving's anchoring and type size per product. The coordinates are the
// same numbers as PREVIEW_POS in assets/pdp-configurator.js, which is their source
// of truth.
//
// The sizes are derived from that file rather than copied. Its values are cqw of a
// stage under object-fit:cover; this one is contain, and its container changes
// aspect between breakpoints, so the absolute numbers do not carry over — but the
// proportions between products do, and those were what was missing here. Every
// handle used to share one size, which put the longest allowed title on a Scrapbook
// A4 across 99% of the album at every screen width. Each value is its counterpart
// there times a single factor, anchored on the one product whose rendering was
// already right: 1.216 for the cover (classic-pocket reading 2.5 there as 3.04cqh
// here) and 0.95 for the spine (2.4 as 2.28cqh, which keeps this PDP's own
// cover-to-spine relationship instead of importing the other one).
//
// Derived is not calibrated. A pass against each mock is still worth doing and
// belongs with the designer; these numbers are what makes the ten uncalibrated
// products proportionate to one another in the meantime. Mini Pocket keeps its own
// measured value and its tight two-line layout.
const PREVIEW_ANCHORS = {
  'mini-pocket': {
    cx: '51%',
    cy: '40.6%',
    coverFs: '5.79cqh',
    coverFont: '"Times New Roman",Times,serif',
    zoomCapa: 1.9,
    engraveTight: true,
  },
  'classic-pocket': { cx: '52.4%', cy: '31.8%', sy: '90.2%', coverFs: '3.04cqh', coverFsM: '4.45cqh', spineFs: '2.28cqh' },
  // Big Pocket shares its framing with Scrapbook A5, and those same numbers are the
  // CSS fallback — so leaving them out here resolved correctly, measured. Written
  // out anyway: relying on the fallback happening to match means editing the CSS
  // default silently moves this one product.
  'big-pocket': { cx: '52%', cy: '32.1%', sy: '90.3%', coverFs: '2.25cqh', coverFsM: '3.71cqh', spineFs: '1.76cqh' },
  'scrap-book-a5': { cx: '52%', cy: '32.1%', sy: '90.3%', coverFs: '2.25cqh', coverFsM: '3.71cqh', spineFs: '1.76cqh' },
  'large-pocket': { cx: '51.8%', cy: '29.2%', sy: '91.3%', coverFs: '1.73cqh', coverFsM: '2.61cqh', coverFsG: '3.59cqh', spineFs: '1.35cqh', spineFsM: '2.04cqh' },
  'scrap-book-a3': { cx: '51.6%', cy: '29.0%', sy: '88.6%', coverFs: '2.07cqh', coverFsM: '2.7cqh', coverFsG: '3.71cqh', spineFs: '1.61cqh', spineFsM: '2.11cqh' },
  'scrap-book-a4': { cx: '51.6%', cy: '29.0%', sy: '88.6%', coverFs: '2.2cqh', coverFsM: '2.98cqh', coverFsG: '4.56cqh', spineFs: '1.72cqh', spineFsM: '2.33cqh' },
  'table-book-a3': { cx: '51.6%', cy: '29.0%', sy: '88.6%', coverFs: '2.07cqh', coverFsM: '2.7cqh', coverFsG: '3.71cqh', spineFs: '1.61cqh', spineFsM: '2.11cqh' },
  'table-book-a4': { cx: '51.6%', cy: '29.0%', sy: '88.6%', coverFs: '2.2cqh', coverFsM: '2.98cqh', coverFsG: '4.56cqh', spineFs: '1.72cqh', spineFsM: '2.33cqh' },
  'table-book-grande': { cx: '51.6%', cy: '29.0%', sy: '88.6%', coverFs: '2.07cqh', coverFsM: '2.7cqh', coverFsG: '3.71cqh', spineFs: '1.61cqh', spineFsM: '2.11cqh' },
  'table-book-midi': { cx: '51.6%', cy: '29.0%', sy: '88.6%', coverFs: '2.2cqh', coverFsM: '2.98cqh', coverFsG: '4.56cqh', spineFs: '1.72cqh', spineFsM: '2.33cqh' },
};

/**
 * Read one edited album back out of a `/cart.js` payload — the pure half of
 * edit hydration, split from the DOM so it is testable the way readGroup is
 * on the porta-retrato module. Null when the key is gone (the bag can change
 * between opening the editor and saving from it).
 *
 * The `_personalizacao` blob is the primary source: it is the reference's own
 * shape and carries the whole engraving state. A "personalize later" line has
 * none — `personalization` comes back null and only variant and add-ons hydrate.
 */
export function readAlbumEditState(cart, editKey) {
  const items = (cart && cart.items) || [];
  const line = items.find((item) => item.key === editKey);
  if (!line) return null;
  const props = line.properties || {};
  const group = props._group;
  const groupItems = group ? items.filter((item) => (item.properties || {})._group === group) : [line];

  let personalization = null;
  // A blob that exists but cannot be read is NOT the same as no blob: the line
  // holds a personalization this reader failed to restore, and letting the
  // save replace the group would overwrite it with a blank state. The flag
  // makes hydration walk away instead. Shape counts as much as syntax: JSON
  // that parses but carries none of the known keys is just as unreadable.
  let blobBroken = false;
  try {
    // Presence decides, not truthiness: a key holding "null", "false", "0" or
    // "" was written by SOMETHING, and none of our writers write those — they
    // must read as broken, never as "no personalization".
    const hasBlob = '_personalizacao' in props;
    const blob = hasBlob && props._personalizacao ? JSON.parse(props._personalizacao) : null;
    // Shape AND types, down to the leaves: a known key carrying an
    // incompatible type (titulo as a string, texto as a number) is just as
    // unreadable as no known key at all — it would hydrate an empty or
    // throwing personalization and let a save replace the real one.
    const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);
    const optionalString = (value) => value === undefined || value === null || typeof value === 'string';
    // For leaves no writer ever nulls (texto, label, hex): a PRESENT key must
    // hold a string — null there is corruption, not a written state. tamanho
    // and simbolo stay optionalString: both writers emit null for them.
    const presentString = (obj, key) => !(key in obj) || typeof obj[key] === 'string';
    // Every writer emits a line WITH its `texto` key — but emptiness is a
    // written state, not corruption: the reference seeds coverLines with ''
    // and serializes them unfiltered (pdp-configurator.js), so a line bought
    // on arm A can legitimately carry { texto: '' }. Only a missing or
    // mistyped key reads as broken.
    const validLines = (value) => value === undefined
      || (Array.isArray(value) && value.every((entry) => isPlainObject(entry) && typeof entry.texto === 'string'));
    // Empty objects are as unwritable as wrong types: each object must carry
    // at least one field a writer actually emits. The bare-string generation
    // only ever serialized a chosen colour's label, so '' is unwritable too.
    const validColor = (value) => (typeof value === 'string' && value.length > 0)
      || (isPlainObject(value)
        && ('label' in value || 'hex' in value)
        && presentString(value, 'label') && presentString(value, 'hex')
        // At least one leaf carries content: writers only serialize a CHOSEN
        // colour, so an all-empty object is as unwritable as an empty one.
        && ((value.label || '').length > 0 || (value.hex || '').length > 0));
    // Exactly one line format: the first writer generation engraves through
    // texto/linha2, the current one through linhas — no writer emits both, and
    // none emits a titulo with neither (hydration would prefer linhas and a
    // save could drop the texto the mix carried).
    const validTitle = (value) => isPlainObject(value)
      && (('texto' in value) !== ('linhas' in value))
      && presentString(value, 'texto') && optionalString(value.linha2)
      && optionalString(value.tamanho) && validLines(value.linhas);
    const validSide = (value) => isPlainObject(value)
      && typeof value.texto === 'string' && optionalString(value.tamanho);
    const keyOk = (key, valueOk) => !(key in blob) || blob[key] === null || valueOk(blob[key]);
    const recognizable = isPlainObject(blob)
      && ('cor' in blob || 'gravacao' in blob || 'titulo' in blob || 'lateral' in blob)
      && keyOk('cor', validColor)
      && keyOk('gravacao', (value) => typeof value === 'string')
      && keyOk('titulo', validTitle)
      && keyOk('lateral', validSide);
    if (hasBlob && !recognizable) {
      blobBroken = true;
    } else if (blob) {
      // Two writer generations live in carts. The original arm-B writer
      // (8edc0d5) serialized `cor` as the bare label and the lines as
      // `titulo.texto`/`titulo.linha2`; a01881f moved to the reference's shape
      // (`cor` object, `titulo.linhas`). Lines added while the old writer was
      // deployed to the experiment theme still have to hydrate whole.
      const title = blob.titulo || {};
      const titleLines = Array.isArray(title.linhas) ? title.linhas : null;
      personalization = {
        colorLabel: typeof blob.cor === 'string' ? blob.cor : (blob.cor && blob.cor.label) || '',
        colorHex: (blob.cor && typeof blob.cor === 'object' && blob.cor.hex) || '',
        foil: blob.gravacao === 'prata' || blob.gravacao === 'dourado' ? blob.gravacao : null,
        titleSize: title.tamanho || null,
        line1: titleLines ? (titleLines[0] && titleLines[0].texto) || '' : title.texto || '',
        // null, not '': an absent second line and an empty one are different
        // decisions — only a present one opens the field and charges the add-on.
        line2: titleLines ? (titleLines[1] && titleLines[1].texto) || null : title.linha2 || null,
        sideText: (blob.lateral && blob.lateral.texto) || '',
        sideSize: (blob.lateral && blob.lateral.tamanho) || null,
      };
    }
  } catch {
    personalization = null;
    blobBroken = true;
  }

  const addonOf = (item) => (item.properties || {})._addon;
  const protectionLine = groupItems.find(
    (item) => addonOf(item) === 'caixa_luva' || addonOf(item) === 'caixa_cristal'
  );
  const musicLine = groupItems.find((item) => addonOf(item) === 'musica');
  let track = null;
  if (musicLine) {
    const musicProps = musicLine.properties || {};
    const songLabel = musicProps['Música'] || '';
    // The dedicated fields first: this arm writes _spotify_name/_spotify_artist
    // exactly so hydration never has to split the display label — a title that
    // itself contains " — " survives whole. Lines written by the classic arm
    // carry only the label, so the split stays as the fallback (its shared
    // contract; the classic reader splits the same way).
    if (musicProps._spotify_name) {
      track = {
        name: musicProps._spotify_name,
        artist: musicProps._spotify_artist || '',
        art: musicProps._spotify_image || '',
        url: musicProps._spotify_url || '',
      };
    } else if (songLabel) {
      // Only an unambiguous label is split — exactly one separator. With more
      // (a title that itself contains " — ") no split can tell name from
      // artist, so the whole label rides as the name: the re-saved line then
      // reproduces the label byte for byte instead of guessing and dropping a
      // piece.
      const songParts = songLabel.split(' — ');
      const unambiguous = songParts.length === 2;
      track = {
        name: unambiguous ? songParts[0] : songLabel,
        artist: unambiguous ? songParts[1] : '',
        art: musicProps._spotify_image || '',
        url: musicProps._spotify_url || '',
      };
    }
  }

  return {
    keys: groupItems.map((item) => item.key),
    variantId: line.variant_id,
    personalization,
    blobBroken,
    protection: protectionLine ? (addonOf(protectionLine) === 'caixa_luva' ? 'luva' : 'cristal') : null,
    musicOn: !!musicLine,
    track,
  };
}

function applyPreviewAnchor(root, handle) {
  const a = PREVIEW_ANCHORS[handle];
  if (!a) return;
  const setVar = (name, value) => {
    if (value != null) root.style.setProperty(name, String(value));
  };
  setVar('--pdp-apple-cx', a.cx);
  setVar('--pdp-apple-cy', a.cy);
  setVar('--pdp-apple-sy', a.sy);
  setVar('--pdp-apple-cover-fs', a.coverFs);
  setVar('--pdp-apple-cover-fs-m', a.coverFsM);
  setVar('--pdp-apple-cover-fs-g', a.coverFsG);
  setVar('--pdp-apple-spine-fs', a.spineFs);
  setVar('--pdp-apple-spine-fs-m', a.spineFsM);
  setVar('--pdp-apple-cover-font', a.coverFont);
  setVar('--pdp-apple-spine-font', a.spineFont);
  setVar('--pdp-apple-zoom-capa', a.zoomCapa);
  setVar('--pdp-apple-zoom-side', a.zoomSide);
  root.classList.toggle('pdp-apple--engrave-tight', !!a.engraveTight);
}

export function init(root, cfg) {
  resizeViewportWithKeyboard();
  applyPreviewAnchor(root, cfg.product.handle);
  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || root).querySelectorAll(sel));

  const strings = cfg.strings || {};

  // Parity with pdp-configurator.js's coverLimit/titleSizes; 23 is the same
  // fallback Liquid uses when the metafield does not define the cover.
  const clCover = (cfg.charLimits && cfg.charLimits.cover) || null;
  const titleSizes = clCover ? SIZE_ORDER.filter((sizeKey) => clCover[sizeKey] != null) : [];
  const coverLimit = (size) => {
    const sizeKey = (size || 'P').toUpperCase();
    return clCover && clCover[sizeKey] != null ? clCover[sizeKey] : 23;
  };
  // Same shape as coverLimit, for the spine. 30 is the fallback the Liquid uses
  // when the metafield defines no spine at all.
  const clSpine = (cfg.charLimits && cfg.charLimits.spine) || null;
  const spineSizes = clSpine ? ['P', 'M'].filter((sizeKey) => clSpine[sizeKey] != null) : [];
  const spineLimit = (size) => {
    const sizeKey = (size || 'P').toUpperCase();
    return clSpine && clSpine[sizeKey] != null ? clSpine[sizeKey] : 30;
  };

  const initialVariant =
    cfg.variants.find((v) => v.id === cfg.product.selectedVariantId) ||
    cfg.variants.find((v) => v.available) ||
    cfg.variants[0] ||
    null;

  const state = {
    fotosVariantId: initialVariant?.id ?? null,
    fotosPrice: initialVariant?.price ?? 0,
    mode: 'later',
    color: 0,
    foil: 'dourado',
    t1: '',
    t2: null,
    size: titleSizes.includes('P') ? 'P' : titleSizes[0] || 'P',
    side: '',
    sideSize: spineSizes[0] || 'P',
    protId: 'none',
    protPrice: 0,
    protVariantId: null,
    music: 0,
    track: null,
    // Photo upsell (scrapbooks). The finish tab is shared by both sizes; photoQtys
    // maps size id → quantity, and a size with no entry is simply not in the order.
    // photoDrafts holds raw keystrokes so the minimum is enforced on blur rather than
    // per keystroke — clamping while typing turns the "1" of "100" into the minimum
    // and swallows the rest.
    photoTab: 'auto',
    photoQtys: {},
    photoDrafts: {},
    renderFlip: false,
    focusField: null,
  };

  const media = $('#pdpAppleMedia');
  const stage = $('#pdpAppleStage');
  const preview = $('#pdpAplPreview');
  const viewFront = $('#pdpAplViewFront');
  const renderA = $('#pdpAplRenderA');
  const renderB = $('#pdpAplRenderB');
  const pvT1 = $('#pdpAplT1');
  const pvT2 = $('#pdpAplT2');
  const pvSide = $('#pdpAplSide');
  // The empty-state label, read from what Liquid rendered — restoring the input's
  // placeholder here would engrave "Ex: …" on the spine.
  const sideEmptyLabel = (pvSide?.textContent || '').trim();
  const galNav = $('#pdpAplGalNav');
  const optFotos = $('#pdpAplOptFotos');
  const optModo = $('#pdpAplOptModo');
  const secCor = $('#pdpAplSecCor');
  const secGrav = $('#pdpAplSecGrav');
  const swatches = $('#pdpAplSwatches');
  const swName = $('#pdpAplSwName');
  const foilSeg = $('#pdpAplFoilSeg');
  const t1Input = $('#pdpAplT1Input');
  const t2Input = $('#pdpAplT2Input');
  const fieldT2 = $('#pdpAplFieldT2');
  const addLine = $('#pdpAplAddLine');
  const count1 = $('#pdpAplCount1');
  const count2 = $('#pdpAplCount2');
  const sizesWrap = $('#pdpAplSizes');
  const sideInput = $('#pdpAplSideInput');
  const countSide = $('#pdpAplCountSide');
  const sideSizesWrap = $('#pdpAplSideSizes');
  const optProt = $('#pdpAplOptProt');
  const optMusic = $('#pdpAplOptMusic');
  const musicPicker = $('#pdpAplMusicPicker');
  const musicSearch = $('#pdpAplMusicSearch');
  const musicSearchWrap = $('#pdpAplMusicSearchWrap');
  const musicHint = $('#pdpAplMusicHint');
  const musicResults = $('#pdpAplMusicResults');
  const musicChosen = $('#pdpAplMusicChosen');
  const chosenArt = $('#pdpAplChosenArt');
  const chosenTitle = $('#pdpAplChosenTitle');
  const chosenArtist = $('#pdpAplChosenArtist');
  const chosenChange = $('#pdpAplChosenChange');
  const orderBox = $('#pdpAplOrderBox');
  const priceBig = $('#pdpAplPriceBig');
  const priceInst = $('#pdpAplPriceInst');
  const pricePix = $('#pdpAplPricePix');
  const barTotal = $('#pdpAplBarTotal');
  const barSub = $('#pdpAplBarSub');
  const addErrorEl = $('#pdpAplAddError');
  const cta = $('#pdpAplCta');
  const secProt = $('#pdpAplSecProt');
  const heroTag = $('.pdp-apple-hero .tag');
  // Outside the section root: the theme's header is what actually covers the top.
  const themeHeader = document.querySelector('.site-header')?.closest('.shopify-section') || null;

  const hasSideText = !!cfg.hasSideText;

  // ── hero tag (installments + Pix) ──
  if (heroTag) {
    // An "infinite" price returns the rule's ceiling (maxCount) with the helper's
    // own defaults — without duplicating the constants here.
    const { count } = resolveInstallments(Number.MAX_SAFE_INTEGER, cfg.installmentsRule);
    heroTag.textContent = `até ${count}x sem juros ou ${cfg.pixPercent}% off via Pix`;
  }

  // ── swatches ──
  cfg.colors.forEach((c, i) => {
    const b = swatches?.children[i];
    if (!b) return;
    b.addEventListener('click', () => selectColor(i));
  });
  if (swName && cfg.colors[0]) swName.textContent = cfg.colors[0].label;

  // Warms the whole set of linens the first time personalization opens. Without it
  // the first colour change waits on a cold download and the preview sits on the
  // old colour for a visible stretch — the crossfade below only swaps once the new
  // image loads. Parity with assets/pdp-configurator.js's Preview._warmed.
  let colorsWarmed = false;
  function warmColorImages() {
    if (colorsWarmed) return;
    colorsWarmed = true;
    (cfg.colorImages || []).forEach((source) => {
      if (!source) return;
      const image = new Image();
      image.src = source;
    });
  }

  // The engraving is placed as a share of the photo, so the layer that carries it
  // has to be the size the photo actually paints at — which means knowing the
  // shot's own aspect. Reading it off the loaded file beats a per-product table:
  // it is exact for every handle, and it follows the art if a shot is ever
  // re-framed. The CSS fallback (900/768, the markup's own dimensions) covers the
  // first paint, before any colour has loaded.
  function adoptPhotoAspect(image, seq) {
    const apply = () => {
      // A stale colour's late load must not overwrite the current selection's
      // aspect — same monotonic token the crossfade already honours.
      if (seq !== renderSeq) return;
      if (!image.naturalWidth || !image.naturalHeight) return;
      root.style.setProperty('--pdp-apple-photo-ar', String(image.naturalWidth / image.naturalHeight));
    };
    image.complete ? apply() : image.addEventListener('load', apply, { once: true });
  }

  // A monotonic token: an earlier colour's late onload (uncached) must not beat the
  // current selection and leave the preview on a colour the state does not hold —
  // only the most recent selection's done() applies the crossfade.
  let renderSeq = 0;
  function selectColor(i) {
    state.color = i;
    $$('.sw', swatches).forEach((el, j) => el.classList.toggle('on', j === i));
    if (swName) swName.textContent = cfg.colors[i].label;
    const src = cfg.colorImages[i];
    if (src) {
      const inEl = state.renderFlip ? renderA : renderB;
      const outEl = state.renderFlip ? renderB : renderA;
      inEl.src = src;
      const seq = ++renderSeq;
      const done = () => {
        if (seq !== renderSeq) return;
        inEl.classList.add('on');
        outEl.classList.remove('on');
      };
      inEl.complete ? done() : (inEl.onload = done);
      state.renderFlip = !state.renderFlip;
      adoptPhotoAspect(inEl, seq);
    }
    refresh();
  }

  // ── gallery ──
  galNav?.addEventListener('click', (e) => {
    const b = e.target.closest('button');
    if (!b) return;
    $$('button', galNav).forEach((el) => el.classList.toggle('on', el === b));
    $$('.photo', stage).forEach((p) => p.classList.toggle('on', p.dataset.g === b.dataset.t));
  });

  function pick(wrap, btn) {
    $$('.opt', wrap).forEach((el) => el.classList.toggle('on', el === btn));
  }

  // ── photos ──
  optFotos?.addEventListener('click', (e) => {
    const b = e.target.closest('.opt');
    if (!b) return;
    const variant = cfg.variants.find((v) => v.id === Number(b.dataset.variantId));
    if (!variant || !variant.available) return;
    state.fotosVariantId = variant.id;
    state.fotosPrice = variant.price;
    pick(optFotos, b);
    syncShippingVariant();
    refresh();
  });

  // The two timings the guided entrance schedules. Kept so the mode handler can
  // cancel them when it goes back to "buy now".
  let glideTimer = null;

  // ── mode ──
  optModo?.addEventListener('click', (e) => {
    const b = e.target.closest('.opt');
    if (!b) return;
    state.mode = b.dataset.m;
    pick(optModo, b);
    const now = state.mode === 'now';
    revealSection(secCor, now);
    revealSection(secGrav, now);
    stage?.classList.toggle('custom', now);
    media?.classList.toggle('custom', now);
    preview?.classList.toggle('on', now);
    if (now) warmColorImages();
    if (now && renderA && !renderA.getAttribute('src')) selectColor(state.color);
    refresh();
    // Leaving "personalize first" cancels whatever the entrance scheduled: neither
    // the guided scroll nor the second line focus may land on a section that is
    // already closing — on a phone both end as a keyboard opened over nothing.
    clearTimeout(glideTimer);
    if (now && isMobile() && secCor && media) glideTimer = setTimeout(glideToColors, GLIDE_DELAY);
  });

  // ── engraving ──
  foilSeg?.addEventListener('mousedown', (e) => {
    if (e.target.closest('button')) e.preventDefault();
  });
  foilSeg?.addEventListener('click', (e) => {
    const b = e.target.closest('button');
    if (!b) return;
    state.foil = b.dataset.foil;
    $$('button', foilSeg).forEach((el) => el.classList.toggle('on', el === b));
    const gold = state.foil === 'dourado';
    [pvT1, pvT2, pvSide].forEach((el) => {
      if (!el) return;
      el.classList.toggle('c-gold', gold);
      el.classList.toggle('c-silver', !gold);
    });
    refresh();
  });

  // The zoom layer is promoted for the RUN of the zoom and demoted once it lands.
  //
  // A standing `will-change:transform` is what keeps the first frame off the main
  // thread (the Android stalls of 24/08), but it also tells the compositor to keep
  // scaling the texture it already rasterized — so at 2x the album is painted from
  // pixels meant for half the size, and the spine in the keyboard band came out
  // soft on the device while the same page looked sharp in a desktop inspector
  // (team, 25/08). Dropping the hint after the transition ends lets the browser
  // re-rasterize at the scale actually on screen.
  let zoomSettle = null;
  function setZoom(zone) {
    if (!viewFront) return;
    // Promoted BEFORE the transform changes, so the run itself is GPU work.
    viewFront.classList.add('zooming');
    viewFront.classList.toggle('zoom-capa', zone === 'capa');
    viewFront.classList.toggle('zoom-side', zone === 'side');
    clearTimeout(zoomSettle);
    // transitionend is the signal, with a timer behind it: a zoom that resolves to
    // the same transform fires no event at all, and the hint would stand forever.
    const settle = () => {
      viewFront.classList.remove('zooming');
      viewFront.removeEventListener('transitionend', onEnd);
    };
    const onEnd = (event) => {
      if (event.target !== viewFront || event.propertyName !== 'transform') return;
      clearTimeout(zoomSettle);
      settle();
    };
    viewFront.addEventListener('transitionend', onEnd);
    zoomSettle = setTimeout(settle, 900);
  }

  // Tapping a control while a text field has focus used to cost the tap itself.
  // The sequence on the device: the press blurs the input, the blur drops the zoom
  // and clears --kb-h, iOS starts dismissing the keyboard, and the reflow that
  // follows moves the control out from under the finger — so the click lands on
  // whatever slid into that spot, and neither the second line nor the size change
  // ever happened. Suppressing the default on mousedown keeps focus in the field:
  // the keyboard never leaves, nothing reflows, and the click reaches its handler.
  function keepFocusOnTap(el) {
    el?.addEventListener('mousedown', (event) => {
      if (state.focusField) event.preventDefault();
    });
  }

  function bindKeyboardZone(input, zone) {
    input?.addEventListener('focus', () => {
      state.focusField = zone;
      state.focusInput = input;
      // The scroll-driven release only arms once the field has been SEEN inside the
      // screen and the settling time has passed — on the device, the tap sets off
      // Safari's own animated scrolling plus the keyboard moving the viewport, and
      // measuring in those in-between frames killed the keyboard at birth (seen on
      // José's iPhone: "the preview drops and the keyboard never opens").
      state.focusSeen = false;
      state.focusAt = performance.now();
      setZoom(zone);
      syncKeyboard(input);
      updateMini();
    });
    input?.addEventListener('blur', () => {
      state.focusField = null;
      state.focusInput = null;
      setZoom(null);
      syncKeyboard();
      updateMini();
    });
  }

  t1Input?.addEventListener('input', (e) => {
    const clean = sanitizeText(e.target.value).slice(0, coverLimit(state.size));
    if (clean !== e.target.value) e.target.value = clean;
    state.t1 = clean;
    if (count1) count1.textContent = `${state.t1.length}/${coverLimit(state.size)}`;
    if (pvT1) {
      pvT1.textContent = state.t1.trim() || cfg.coverDefaultText || 'MEMÓRIAS';
      pvT1.classList.toggle('empty', !state.t1.trim());
    }
    refresh();
  });
  bindKeyboardZone(t1Input, 'capa');

  keepFocusOnTap(addLine);
  addLine?.addEventListener('click', () => {
    if (state.t2 === null) {
      state.t2 = '';
      if (fieldT2) fieldT2.classList.add('open');
      addLine.textContent = `− ${strings.removeLine || 'Remover linha'} (− ${money(cfg.linePrice || 0)})`;
      // Focus inside the gesture, not after the transition. Waiting for the field to
      // finish opening read better on a desktop browser, but on a phone it costs the
      // keyboard entirely: iOS only opens it for a focus that happens in the same
      // task as the tap, so the customer had to tap the field a second time. The
      // field finishes opening underneath the keyboard, which is the cheaper trade.
      //
      // The flush is what makes that focus stick. Collapsed, the field's inner box
      // is visibility:hidden, and a hidden element cannot take focus — so adding the
      // class and calling focus() in the same block asked for focus while the old
      // computed value was still in place, and the call was dropped on the floor.
      // Reading a layout property forces the style resolution that flips it visible
      // first, without waiting a frame and losing the gesture.
      if (fieldT2) void fieldT2.offsetHeight;
      // preventScroll, because the page must not move for this. The field opens
      // directly under line one, which is already on screen, so there is nothing
      // to scroll to — but focusing an input makes the browser bring it into view
      // on its own, and that native scroll is what was still jumping the page
      // after the anchor was taught to stay put. Focus itself still happens inside
      // the gesture, which is what the keyboard depends on.
      t2Input?.focus({ preventScroll: true });
    } else {
      // Hand the keyboard back to line one rather than letting it close: the field
      // about to collapse may be the focused one, and blur here would drop the zoom
      // and shift the page under the customer's finger.
      if (t2Input && document.activeElement === t2Input) t1Input?.focus({ preventScroll: true });
      state.t2 = null;
      if (fieldT2) fieldT2.classList.remove('open');
      addLine.textContent = `+ ${strings.addLine || 'Adicionar linha'} (+ ${money(cfg.linePrice || 0)})`;
      if (t2Input) t2Input.value = '';
      if (pvT2) pvT2.style.display = 'none';
    }
    refresh();
  });
  t2Input?.addEventListener('input', (e) => {
    const clean = sanitizeText(e.target.value).slice(0, coverLimit(state.size));
    if (clean !== e.target.value) e.target.value = clean;
    state.t2 = clean;
    if (count2) count2.textContent = `${state.t2.length}/${coverLimit(state.size)}`;
    if (pvT2) {
      pvT2.style.display = state.t2.trim() ? '' : 'none';
      pvT2.textContent = state.t2;
    }
    refresh();
  });
  bindKeyboardZone(t2Input, 'capa');

  // Re-trims the texts to the selected size's limit and keeps maxlength, counters
  // and preview in step (parity with onTitleSize; the second line shares the
  // cover's limit, as in the reference).
  function applyCoverLimit() {
    const limit = coverLimit(state.size);
    [t1Input, t2Input].forEach((input) => {
      if (input) input.maxLength = limit;
    });
    state.t1 = state.t1.slice(0, limit);
    if (t1Input && t1Input.value !== state.t1) t1Input.value = state.t1;
    if (count1) count1.textContent = `${state.t1.length}/${limit}`;
    if (pvT1) {
      pvT1.textContent = state.t1.trim() || cfg.coverDefaultText || 'MEMÓRIAS';
      pvT1.classList.toggle('empty', !state.t1.trim());
    }
    if (state.t2 !== null) {
      state.t2 = state.t2.slice(0, limit);
      if (t2Input && t2Input.value !== state.t2) t2Input.value = state.t2;
      if (count2) count2.textContent = `${state.t2.length}/${limit}`;
      if (pvT2) {
        pvT2.style.display = state.t2.trim() ? '' : 'none';
        pvT2.textContent = state.t2;
      }
    }
  }

  keepFocusOnTap(sizesWrap);
  sizesWrap?.addEventListener('click', (e) => {
    const b = e.target.closest('button');
    if (!b) return;
    state.size = b.dataset.s;
    $$('button', sizesWrap).forEach((el) => el.classList.toggle('on', el === b));
    // Both lines, not just the first: the size is a property of the engraving, and
    // choosing M or G used to leave line two behind at its fixed size.
    [pvT1, pvT2].forEach((el) => {
      el?.classList.toggle('size-m', state.size === 'M');
      el?.classList.toggle('size-g', state.size === 'G');
    });
    applyCoverLimit();
    refresh();
  });

  sideInput?.addEventListener('input', (e) => {
    const sideMax = sideInput.maxLength > 0 ? sideInput.maxLength : Infinity;
    const clean = sanitizeText(e.target.value).slice(0, sideMax);
    if (clean !== e.target.value) e.target.value = clean;
    state.side = clean;
    if (countSide) countSide.textContent = `${state.side.length}/${sideInput.maxLength}`;
    if (pvSide) {
      if (state.side.trim()) {
        pvSide.textContent = state.side;
        pvSide.classList.remove('empty');
      } else {
        pvSide.textContent = sideEmptyLabel;
        pvSide.classList.add('empty');
      }
    }
    refresh();
  });
  // Changing the size changes how much fits, so the text is re-trimmed to the new
  // limit — the same thing onSideSize does in the reference. Without it the field
  // would keep more characters than the engraver accepts for the chosen size.
  function applySpineLimit() {
    const limit = spineLimit(state.sideSize);
    if (sideInput) sideInput.maxLength = limit;
    state.side = state.side.slice(0, limit);
    if (sideInput && sideInput.value !== state.side) sideInput.value = state.side;
    if (countSide) countSide.textContent = `${state.side.length}/${limit}`;
    if (pvSide) {
      if (state.side.trim()) {
        pvSide.textContent = state.side;
        pvSide.classList.remove('empty');
      } else {
        pvSide.textContent = sideEmptyLabel;
        pvSide.classList.add('empty');
      }
    }
  }

  // The size the preview renders at and the limit the field enforces are one
  // decision, so they move together — at startup and on every change alike.
  function applySpineSize() {
    pvSide?.classList.toggle('size-m', state.sideSize === 'M');
    applySpineLimit();
  }

  // The preview starts on whatever size the state starts on, which is not always P:
  // a product whose metafield defines only M opens on M, and with one size there is
  // no selector to click, so the class would never be applied and the spine would
  // render at P's font against M's limit.
  applySpineSize();

  keepFocusOnTap(sideSizesWrap);
  sideSizesWrap?.addEventListener('click', (event) => {
    const button = event.target.closest('button');
    if (!button) return;
    state.sideSize = button.dataset.s;
    $$('button', sideSizesWrap).forEach((el) => el.classList.toggle('on', el === button));
    applySpineSize();
    refresh();
  });

  bindKeyboardZone(sideInput, 'side');

  // ── keyboard open (mobile): the preview band measured by visualViewport ──
  const vv = window.visualViewport;
  const KB_BAND_MIN = 120;
  // The band used to take every pixel between the header and the field, and on a
  // phone that is most of the screen — QA (24/08, iPhone) read it as the preview
  // shouting over the one line being edited. 170 matches the compact strip of the
  // original three-state mock: enough album to see the engraving land, and the
  // leftover room becomes breathing space above the field instead of dead white
  // inside the band.
  const KB_BAND_MAX = 170;
  const KB_ACCESSORY = 44;
  const KB_GAP = 10;
  const KB_MIN_HIDDEN = 120;
  const KB_SCROLL_MS = 1050;
  // How long after the keyboard first reads as open before the band and the
  // travel are allowed in. The native rise lands its viewport resize within
  // ~200ms on the measured devices; 400 gives Safari's own reveal a head start
  // too, so the one glide that follows runs against numbers that hold still.
  const KB_SETTLE_MS = 400;
  // Which zone the page is currently parked for, not merely whether it is parked:
  // line one and line two are both the cover zone and share a destination, so
  // moving between them must not travel anywhere.
  let kbAnchoredZone = null;
  let kbSettleTimer = null;
  let kbSettleDone = false;
  // The band height as it stands right now, not as it stood when the travel began.
  let kbBandHeight = 0;

  // Where the top of the visible window falls in the page's own coordinates, for the
  // travel's destination only. Nothing pinned reads it — the three fixed and sticky
  // tops are written against the layout viewport, the way they were before any of
  // this, because compensating them is what put the preview in the wrong place on
  // iOS. This is the other half: getBoundingClientRect answers in layout coordinates,
  // so parking a field "just below the band" means nothing unless it also knows where
  // the window starts.
  //
  // Safari does slide it, and slides it gradually. Read off an iPhone 16 through
  // BrowserStack, spine field, one run:
  //
  //   t  60  vv 741->436  off 306   the frame it resizes on
  //   t 201  vv 436       off   7
  //   t 421  vv 436       off  80
  //   t 721  vv 436       off 183
  //   t1201  vv 436       off 187   field bottom 206, in a window 436 tall
  //
  // 206 with a band about 290 deep is the field sitting behind the preview, which is
  // what QA reported. The 306 at t60 is not a position, it is the artefact of the
  // frame the viewport resized on — same shape as Chrome's transient — so the value
  // is only taken once the height has settled since the previous event.
  let screenTopPx = 0;
  let lastViewportHeight = vv ? vv.height : 0;
  let publishedScreenTop = null;

  function noteScreenTop() {
    if (!vv) return;
    const settled = vv.height === lastViewportHeight;
    lastViewportHeight = vv.height;
    if (!settled) return;
    screenTopPx = Math.round(vv.offsetTop);
    // Published for one reader: the keyboard band's own top. The band is fixed
    // against the layout viewport, and Safari's slide walks the visible window
    // down past it — measured on the side field (24/08, iPhone 16e): off 2 -> 17
    // -> 42 -> 76 -> 109 -> 136 across the travel, and a 170px band behind a
    // 136px slide keeps 34px on screen. The travel's destination already trusts
    // this exact settled number; painting the band from it keeps the two in one
    // coordinate system. This is NOT the reverted raw-offset variable: that fed
    // the per-frame offset — one-frame spikes of 98 and 306 — to every pinned
    // top, and the spikes are precisely what the settle guard above filters out.
    if (screenTopPx !== publishedScreenTop) {
      publishedScreenTop = screenTopPx;
      document.documentElement.style.setProperty('--screen-top', screenTopPx + 'px');
    }
  }

  // The viewport as it stands with no keyboard over it — the ruler the keyboard is
  // measured against.
  //
  // It used to be measured against window.innerHeight, on the reading that the
  // layout viewport is a fixed height and only the visual one shrinks. That holds on
  // iOS. It does not on Chrome for Android, where innerHeight follows the VISUAL
  // viewport and shrinks along with vv.height: `innerHeight - vv.height` stays near
  // zero there however large the keyboard, so the band never engaged at all. Read
  // frame by frame off the feedback video of 21/08, the preview never once pinned —
  // it scrolled with the page, and whether it showed whole, sliced or not at all was
  // only where the page happened to be parked when the keyboard arrived. That is the
  // "sometimes it comes nicely, sometimes it comes broken" in the recording.
  //
  // Held as a maximum so a transient shrink cannot poison it — switching fields with
  // the keyboard already up passes through blur, and a viewport event landing in that
  // window would otherwise record the shrunken height as the resting one. Rotation
  // changes the width, and that resets it rather than keeping a portrait height as
  // the ruler for landscape.
  let restingWidth = vv ? vv.width : 0;
  let restingHeight = vv ? vv.height : 0;

  function noteRestingViewport() {
    if (!vv) return;
    if (vv.width !== restingWidth) {
      restingWidth = vv.width;
      restingHeight = vv.height;
      return;
    }
    if (!state.focusField && vv.height > restingHeight) restingHeight = vv.height;
  }

  function syncKeyboard(focused) {
    if (!vv || !media) return;
    noteRestingViewport();
    noteScreenTop();
    // The larger of the two rulers: on iOS innerHeight is the stable full height and
    // wins, which keeps that platform reading exactly as it did; on Android it has
    // collapsed with the keyboard and the resting height wins.
    const hidden = Math.max(window.innerHeight, restingHeight) - vv.height;
    const open = isMobile() && state.mode === 'now' && !!state.focusField && hidden > KB_MIN_HIDDEN;
    // The band is fixed, so the preview leaves the flow and everything below it comes
    // up by its height — the same hole .mini leaves behind, and nobody was holding
    // this one. Measured on an iPhone 16 through BrowserStack: at the frame the band
    // engages, the focused field jumped 404px up the screen, and the travel spent the
    // rest of its 700ms chasing a destination that had moved with it. That is why two
    // rounds of easing changed almost nothing — the curve was being multiplied by a
    // distance that started near zero and grew:
    //
    //   elapsed   the curve asks for   the page had moved
    //   25%       38%                  3%
    //   56%       76%                  32%
    //   76%       92%                  70%
    //
    // Held in the same task the class lands in, and measured before it, so the page is
    // laid out once and the destination stands still.
    // The staybar and the WhatsApp float leave the moment the keyboard is
    // coming, ahead of the settle wait below — on Android they ride the
    // shrinking viewport up over the field otherwise.
    document.body.classList.toggle('pdp-apple-editing', open);
    // The lock lifts with the keyboard, and the header has to answer now rather than
    // wait for the next finger movement — she may well have finished scrolling.
    stickyHeader?.sync();
    const docEl = document.documentElement;
    if (!open) {
      media.classList.remove('kb');
      // The target, not the class: mid-swap the class trails it by a frame, and
      // handing the room back there would drop the page under a preview that is
      // about to be out of the flow again.
      if (!miniTarget) root.style.removeProperty('--mini-hold');
      docEl.style.removeProperty('--kb-h');
      if (kbSettleTimer) {
        clearTimeout(kbSettleTimer);
        kbSettleTimer = null;
      }
      // Forget the parking spot only once the keyboard has actually gone. Adding
      // or removing the second line moves focus between two inputs of the same
      // zone, and that hand-off passes through here with focusField briefly null;
      // clearing on that made the next focus re-run the whole travel, so the page
      // scrolled for a change that had not moved the destination at all. The
      // settle mark survives the hand-off for the same reason: the keyboard is
      // already up, there is no rise left to wait for.
      if (hidden <= KB_MIN_HIDDEN) {
        kbAnchoredZone = null;
        kbSettleDone = false;
      }
      return;
    }
    // Sequenced, not simultaneous (QA 24/08, iPhone 16e): the native keyboard
    // rise, Safari's gradual reveal of the field, the band engaging and the
    // travel all played in the same window and read as truncado — three motions
    // over each other, and the glide chasing an offset that was still growing
    // (off 0 -> 108 across the 1050ms on the 16e). The keyboard goes first,
    // alone; once it has settled the band enters and the one travel runs.
    if (!kbSettleDone) {
      if (!kbSettleTimer) {
        kbSettleTimer = setTimeout(() => {
          kbSettleTimer = null;
          kbSettleDone = true;
          syncKeyboard();
        }, KB_SETTLE_MS);
      }
      return;
    }
    if (!media.classList.contains('kb')) {
      root.style.setProperty('--mini-hold', Math.round(media.getBoundingClientRect().height) + 'px');
      media.classList.add('kb');
    }
    updateHeader();
    const top = parseInt(getComputedStyle(docEl).getPropertyValue('--hdr'), 10) || 0;
    // Anchored by the focused zone: with focus on the SPINE, measuring and
    // scrolling by the cover block left the preview off screen on the device
    // (Safari scrolls to the field, this code scrolled back to the cover).
    const zoneAnchor = state.focusField === 'side' ? sideInput?.closest('.field') : null;
    const anchorBottom = (zoneAnchor || addLine || t1Input)?.getBoundingClientRect().bottom || 0;
    const anchorTop = (zoneAnchor || foilSeg || t1Input)?.getBoundingClientRect().top || 0;
    const bloco = anchorBottom - anchorTop;
    const band = Math.max(KB_BAND_MIN, Math.min(KB_BAND_MAX, Math.round(vv.height - KB_ACCESSORY - top - KB_GAP - bloco)));
    docEl.style.setProperty('--kb-h', band + 'px');
    // Kept where the travel below can reach it. The band is recomputed on every
    // viewport event as the keyboard rises; the travel used to take a copy on its
    // first frame and park the field against that, and the keyboard is still moving
    // then. Measured on the device, spine field: the copy was taken at vv 466 and the
    // keyboard settled at 402, and the field came to rest 422px down a 402px window —
    // 64px out of sight, which is the difference exactly.
    kbBandHeight = band;
    const el = focused || document.activeElement;
    if (kbAnchoredZone === state.focusField || !el || el.tagName !== 'INPUT') return;
    kbAnchoredZone = state.focusField;
    // Glided, not jumped. This used to land the whole travel in a single frame,
    // and on the device it read as the page having broken rather than moved —
    // there is no motion to follow, so you lose where you were. Same hand-rolled
    // scroll, symmetric curve and duration as the guided entrance: 420ms was
    // tried first, on the theory that the travel should be done by the time the
    // keyboard settled, and on the device it still read as hurried. The page and
    // the entrance now move at one speed.
    requestAnimationFrame(() => {
      const alvo = zoneAnchor || foilSeg;
      // Where the top of the screen falls in the page's own coordinates. Zero on iOS
      // and whenever Chrome scrolls the document instead; 255px in the runs where it
      // slid the visual viewport and left the document standing. getBoundingClientRect
      // answers in layout coordinates, so without this the field is parked below a
      // band that is itself off the top of the screen.
      //
      // Read per frame, not once. Chrome's slide does not arrive with the touch: on
      // the device it landed at t473 (117px) and t561 (255px), by which time the
      // travel was already running against an offset of 0 — and the field came to
      // rest under the band instead of below it. Everything else here is read once on
      // purpose, but this is the one number that is still moving.
      // The band height is the other number still moving — the viewport is
      // shrinking while the keyboard rises — so it rides the same per-frame rule.
      const bandNow = () => kbBandHeight || band;
      // Recomputed per frame, and stable while it runs: scrolling moves the page
      // and the anchor's viewport top by the same amount, so this stays put.
      const targetY = () => scrollY + (alvo?.getBoundingClientRect().top ?? 0) - (screenTopPx + top + bandNow() + KB_GAP);
      // From wherever the page stands, not from the touch. The travel used to
      // anchor to the scroll position read at focus, undoing Safari's throw on
      // its first frame — right when the travel raced the throw. Sequenced
      // behind the settle wait, the throw has already been on screen for 400ms,
      // and gliding back over it would replay it backwards before travelling
      // forward again.
      if (Math.abs(targetY() - scrollY) > 2) glideTo(targetY, KB_SCROLL_MS);
    });
  }
  if (vv) {
    // The offset is published from here as well as from updateHeader: the visual
    // viewport slides on its own, with the document standing still, so a scroll
    // handler hung on the window never hears about it.
    vv.addEventListener('resize', () => syncKeyboard());
    vv.addEventListener('scroll', () => syncKeyboard());
  }

  // ── protection ──
  optProt?.addEventListener('click', (e) => {
    const b = e.target.closest('.opt');
    if (!b) return;
    state.protId = b.dataset.protId;
    state.protPrice = Number(b.dataset.p);
    state.protVariantId = b.dataset.variantId ? Number(b.dataset.variantId) : null;
    pick(optProt, b);
    refresh();
  });

  // ── music ──
  optMusic?.addEventListener('click', (e) => {
    const b = e.target.closest('.opt');
    if (!b) return;
    state.music = Number(b.dataset.mu);
    pick(optMusic, b);
    musicPicker?.classList.toggle('open', state.music === 1);
    if (state.music === 1 && !state.track) setTimeout(() => musicSearch?.focus(), 380);
    refresh();
  });

  // The classic PDP's Spotify client, shared — see the module header for why
  // the catalogue has to be Spotify's.
  const spotifySearch = createSpotifySearch(cfg.spotify || {});

  let musicTimer;
  // A monotonic token: a slow response for an earlier query must not overwrite
  // the results of the current one (same guard the classic runner keeps).
  let musicSearchSeq = 0;
  // The Liquid-rendered hint, restored when the box empties: with the token
  // guard, a response arriving after the box was cleared returns early and
  // would otherwise leave "Buscando…" standing forever.
  const musicHintDefault = musicHint?.textContent || '';
  musicSearch?.addEventListener('input', (e) => {
    const q = e.target.value.trim();
    clearTimeout(musicTimer);
    if (q.length < 2) {
      musicSearchSeq += 1;
      if (musicResults) musicResults.innerHTML = '';
      if (musicHint) musicHint.textContent = musicHintDefault;
      return;
    }
    musicTimer = setTimeout(() => searchTracks(q), 320);
  });

  async function searchTracks(q) {
    const seq = (musicSearchSeq += 1);
    if (musicHint) musicHint.textContent = 'Buscando…';
    try {
      const list = await spotifySearch(q, 6);
      if (seq !== musicSearchSeq) return;
      renderTracks(list);
      if (musicHint) musicHint.textContent = list.length ? 'Toque na música para escolher.' : 'Nenhuma música encontrada. Tente outro termo.';
    } catch {
      if (seq !== musicSearchSeq) return;
      if (musicHint) musicHint.textContent = 'Não foi possível buscar agora. Tente novamente.';
    }
  }

  function renderTracks(list) {
    if (!musicResults) return;
    musicResults.innerHTML = '';
    list.forEach((t) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'track';
      const img = document.createElement('img');
      img.src = t.image || '';
      img.alt = '';
      const txt = document.createElement('span');
      txt.className = 'track__txt';
      const bEl = document.createElement('b');
      bEl.textContent = t.name || '';
      const span = document.createElement('span');
      span.textContent = t.artist || '';
      txt.append(bEl, span);
      b.append(img, txt);
      b.addEventListener('click', () => chooseTrack({ name: t.name, artist: t.artist, art: t.image || '', url: t.url || '' }));
      musicResults.appendChild(b);
    });
  }

  function chooseTrack(t) {
    state.track = t;
    if (chosenArt) chosenArt.src = t.art;
    if (chosenTitle) chosenTitle.textContent = t.name;
    if (chosenArtist) chosenArtist.textContent = t.artist;
    musicChosen?.classList.add('on');
    if (musicSearchWrap) musicSearchWrap.style.display = 'none';
    if (musicHint) musicHint.style.display = 'none';
    if (musicResults) musicResults.innerHTML = '';
    refresh();
  }

  chosenChange?.addEventListener('click', () => {
    state.track = null;
    musicChosen?.classList.remove('on');
    if (musicSearchWrap) musicSearchWrap.style.display = '';
    if (musicHint) {
      musicHint.style.display = '';
      musicHint.textContent = 'Digite o nome da música que vai tocar ao abrir seu álbum.';
    }
    if (musicSearch) {
      musicSearch.value = '';
      musicSearch.focus();
    }
    refresh();
  });

  // ── the preview mini/floating states (mobile) ──
  const MINI_ENTER = -38;
  const MINI_EXIT = 82;
  const MINI_STUCK_TOL = 8;
  // On while the "personalize first" sections are opening. See the guard in
  // updateMini and in revealSection, just below.
  let revealInFlight = false;
  let revealSettleTimer = null;

  // Shrunk, the preview is fixed rather than stuck, so it leaves the flow and
  // everything below its resting place would jump up by its height. The room is held
  // open in the same task the class lands in, so the page is laid out once and
  // nothing moves. Measured rather than derived: .media keeps the same box either
  // way, because what shrinks is the stage, by transform.
  //
  // ── Why two classes and two frames ─────────────────────────────────────────
  // `mini-fixed` takes the preview out of the flow; `mini` shrinks the stage.
  // They used to be one class, and WebKit threw the growth away: on the iPhone
  // the thumbnail jumped straight to full size instead of gliding back over its
  // 650ms (video from the team, 25/08 — 67ms between the two frames, nothing in
  // between). Changing an element's position in the same style recalculation as
  // a descendant's transform is what does it; the same tap instrumented in
  // Chromium plays every frame of the curve, so the animation was never the
  // problem. Positioning therefore settles one frame BEFORE the scale moves,
  // entering and leaving alike, and the transition starts against a box that is
  // already standing still.
  //
  // The intermediate frame is invisible either way: .media keeps the same box
  // fixed or stuck (the thumbnail's top is the stuck top), and the room below is
  // already held open by --mini-hold.
  let miniTarget = false;
  function setMini(on, height, immediate) {
    if (on) root.style.setProperty('--mini-hold', Math.round(height) + 'px');
    else root.style.removeProperty('--mini-hold');
    if (miniTarget === on && media.classList.contains('mini') === on) return;
    miniTarget = on;
    media.classList.toggle('mini-fixed', on);
    if (immediate) {
      media.classList.toggle('mini', on);
      return;
    }
    requestAnimationFrame(() => {
      // A newer decision landed while this one waited for its frame.
      if (miniTarget !== on) return;
      media.classList.toggle('mini', on);
    });
  }

  function updateMini() {
    if (!media) return;
    if (!isMobile() || state.mode !== 'now') {
      // immediate: this is a mode change, not the shrink — there is no growth to
      // watch and a frame of half-state would be a frame of wrong layout.
      setMini(false, 0, true);
      media.classList.remove('floating', 'kb');
      return;
    }
    if (media.classList.contains('kb')) {
      // Not setMini(false): that would give the room back, and while the band is open
      // the preview is out of the flow and the room is exactly what stops everything
      // below it from jumping. syncKeyboard owns --mini-hold for as long as the band
      // is up, and hands it back when the band closes.
      miniTarget = false;
      media.classList.remove('mini', 'mini-fixed', 'floating');
      return;
    }
    const hdr = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--hdr'), 10) || 0;
    // The preview sticks with the CSS's room above the header; without adding that
    // in here the threshold would sit below the resting position and the "floating"
    // state would never engage.
    const stickGap = parseInt(getComputedStyle(root).getPropertyValue('--pdp-stick-gap'), 10) || 0;
    const rect = media.getBoundingClientRect();
    media.classList.toggle('floating', rect.top <= hdr + stickGap + MINI_STUCK_TOL);
    // While the sections open, the page geometry does not count yet: secProt sits
    // higher than it will end up and the 'mini' threshold would fire on its own
    // mid-transition. The state is computed again once it finishes.
    if (revealInFlight) return;
    if (state.focusField) {
      setMini(false);
      return;
    }
    if (!secProt) return;
    const protTop = secProt.getBoundingClientRect().top;
    // The TARGET, not the class: for one frame after a decision the class is
    // still catching up, and reading it there would flip the hysteresis to the
    // wrong threshold and undo the decision that was in flight.
    setMini(protTop < rect.bottom + (miniTarget ? MINI_EXIT : MINI_ENTER), rect.height);
  }

  // Tapping the shrunken preview is the way back into editing: the page travels to
  // the engraving section, and the preview grows again on the way, as the threshold
  // above is crossed. Bound to the stage rather than to .media, which is
  // pointer-events:none while shrunk so the page under the thumbnail stays usable.
  //
  // The travel can be anything from a screen to the whole page — the thumbnail stays
  // fixed to the end now — so one fixed duration would make the long way a blur and
  // the short way sluggish. It is paced by distance instead, inside the bounds that
  // keep it readable either way.
  const MINI_TAP_MS_PER_PX = 0.55;
  const MINI_TAP_MIN_MS = 450;
  const MINI_TAP_MAX_MS = 900;
  stage?.addEventListener('click', () => {
    if (!media?.classList.contains('mini')) return;
    const heading = (secGrav || secCor)?.querySelector('h2');
    if (!heading) return;
    // The destination the guided entrance already aims at, one section further down:
    // the title left just below the preview. Measured against the preview's bottom
    // rather than against the header, because the preview is 259px of stuck chrome
    // in its own right — aimed at the header, "Escolha o texto da capa e da lateral"
    // arrived underneath it (QA, 21/08). The preview's box is the same shrunken or
    // not, so this reads the same before and after it grows back on the way.
    const targetY = () => scrollY + heading.getBoundingClientRect().top - media.getBoundingClientRect().bottom - COLORS_GAP;
    const distance = Math.abs(targetY() - scrollY);
    if (distance < 4) return;
    if (prefersReducedMotion()) {
      scrollTo({ top: targetY(), behavior: 'instant' });
      updateMini();
      return;
    }
    const duration = Math.min(MINI_TAP_MAX_MS, Math.max(MINI_TAP_MIN_MS, Math.round(distance * MINI_TAP_MS_PER_PX)));
    glideTo(targetY, duration, updateMini);
  });

  // The entrance is a CSS transition, so on closing the hidden attribute can only
  // come back once it ends — otherwise display:none cuts the path off in the first
  // frame. The per-element token keeps one section's closing from cancelling the
  // other's: the two move together.
  const revealTokens = new WeakMap();
  function revealSection(section, open) {
    if (!section) return;
    const token = (revealTokens.get(section) || 0) + 1;
    revealTokens.set(section, token);
    if (open) {
      section.hidden = false;
      // Reading a layout metric between dropping hidden and adding the class forces
      // the reflow: without it the browser merges the two states into one frame and
      // there is nothing left to transition.
      void section.offsetHeight;
      section.classList.add('open');
      revealInFlight = true;
      // Reopening within 450ms let the previous run's timeout release the lock in
      // the middle of the new transition, and updateMini went back to deciding on
      // geometry that was still moving. Each opening invalidates the one before.
      clearTimeout(revealSettleTimer);
      revealSettleTimer = setTimeout(() => {
        revealInFlight = false;
        updateMini();
      }, REVEAL_MS);
      return;
    }
    section.classList.remove('open');
    if (prefersReducedMotion()) {
      section.hidden = true;
      return;
    }
    setTimeout(() => {
      if (revealTokens.get(section) === token) section.hidden = true;
    }, REVEAL_MS);
  }

  // The colour grid opens underneath the pinned preview on mobile: without
  // repositioning, the first rows of linen are born hidden behind it and she never
  // notices they exist.
  function glideToColors() {
    const heading = secCor?.querySelector('h2');
    if (!heading || !media) return;
    // A live destination: the scroll position that leaves the colours' title just
    // below the preview. While the sections open and the stage changes aspect
    // ratio, both sides are still moving — hence the recompute every frame.
    const targetY = () => scrollY + heading.getBoundingClientRect().top - media.getBoundingClientRect().bottom - COLORS_GAP;
    if (Math.abs(targetY() - scrollY) <= 4) return;
    if (prefersReducedMotion()) {
      scrollTo({ top: targetY(), behavior: 'instant' });
      return;
    }
    glideTo(targetY, GLIDE_MS, updateMini);
  }

  function updateHeader() {
    // --hdr is how much chrome is pinned at the top of the screen, and everything
    // that has to clear it reads this one value: the stuck preview, the editing
    // band, and the travel the keyboard triggers. This PDP used to fly a header of
    // its own as well, so the value was the taller of the two; that header is gone
    // (20/08 QA) and the theme's sticky one is all there is to clear.
    const themeBottom = themeHeader ? themeHeader.getBoundingClientRect().bottom : 0;
    const pinned = Math.max(0, Math.round(themeBottom));
    document.documentElement.style.setProperty('--hdr', pinned + 'px');
  }


  addEventListener(
    'scroll',
    () => {
      // iOS does not blur the input when you scroll with a finger, so the preview
      // stayed "full" (floating + zoom) over the rest of the page until a tap
      // outside. When the focused field leaves the screen entirely, we release the
      // focus — the teardown is blur's normal path. syncKeyboard's programmatic
      // scroll never triggers this: it anchors the field inside the screen, not out.
      //
      // "The screen" is the resting ruler, not innerHeight. Under
      // interactive-widget=resizes-content Android's innerHeight collapses with the
      // keyboard (777 -> 402 on the device, 24/08) while the tapped field still sits
      // below the shrunken window waiting for the travel to bring it up — fld 612 on
      // the first tap of an unscrolled page. Read against the collapsed height,
      // "left the screen" came true mid-travel and this released the focus at
      // t~900ms: the keyboard closed on every first tap, and only the retap — made
      // after the glide had already parked the page — survived. Same expression
      // syncKeyboard subtracts from: on iOS it IS innerHeight, so nothing changes
      // there.
      if (state.focusInput) {
        const r = state.focusInput.getBoundingClientRect();
        const fora = r.bottom < 0 || r.top > Math.max(innerHeight, restingHeight);
        if (!fora) state.focusSeen = true;
        else if (state.focusSeen && performance.now() - state.focusAt > 600) state.focusInput.blur();
      }
      updateMini();
      updateHeader();
    },
    { passive: true }
  );
  addEventListener('resize', () => {
    updateMini();
    updateHeader();
  });

  // A field of this PDP holding focus — the state where the page scrolls itself.
  const isEngraving = () => {
    const active = document.activeElement;
    return !!active && root.contains(active) && /^(input|textarea)$/i.test(active.tagName);
  };

  // The header leaves on the way down and comes back on the way up. updateHeader is
  // handed over as the measurement hook: the preview and the editing band are anchored
  // to --hdr, which is this header's bottom edge, so they rise and fall with it.
  //
  // Loaded by the versioned URL from the island, never by a relative path: a relative
  // import carries no ?v= and the CDN answers the copy it cached the first time the
  // file was published. Fail-open — a fetch that does not land costs the mechanic, not
  // the page, and every stickyHeader call site is optional-chained.
  let stickyHeader = null;
  if (cfg.assets?.stickyHeader) {
    import(cfg.assets.stickyHeader)
      .then(({ initStickyHeader }) => {
        stickyHeader = initStickyHeader(themeHeader, {
          onMove: updateHeader,
          // The focused field, not only the keyboard band: the band is raised after
          // the keyboard is measured, while the page starts scrolling itself the
          // moment the field takes focus. Between the two, that travel was moving the
          // header. The band stays in the condition because focus can leave the field
          // while the band is still up.
          locked: () => document.body.classList.contains('pdp-apple-editing') || isEngraving(),
        });
      })
      .catch(() => {});
  }

  // ── popups (native <dialog>, product.liquid's own contract) ──
  const openModal = (m) => {
    if (!m) return;
    // A lazy image inside display:none does not fire when revealed; promote it.
    $$('img[loading="lazy"]', m).forEach((img) => {
      img.loading = 'eager';
    });
    if (typeof m.showModal === 'function') {
      if (!m.open) m.showModal();
    } else m.setAttribute('open', '');
  };
  const closeModal = (m) => {
    if (typeof m.close === 'function') m.close();
    else m.removeAttribute('open');
  };
  $('#pdpAplOpenFotosModal')?.addEventListener('click', () => openModal(root.querySelector('#pdpAplFotosModal')));
  $$('.seemore').forEach((b) => {
    b.addEventListener('click', () => openModal(root.querySelector('#' + b.dataset.modal)));
  });
  $$('.modal').forEach((m) => {
    m.addEventListener('click', (e) => {
      // The dialog itself is the container outside the card — a click on it (or on
      // the backdrop, which also targets it) closes, as the old backdrop did.
      if (e.target === m || e.target.closest('[data-close]')) closeModal(m);
    });
  });

  // ── accordions ──
  $$('.acc__head').forEach((h) => {
    h.addEventListener('click', () => {
      root.querySelector('#' + h.dataset.acc)?.classList.toggle('open');
    });
  });

  // ── shipping ──
  // The postcode, the button and the quote belong to the shared snippet
  // (prazo-estimator), which talks to the real endpoint. All we do here is keep the
  // variant current: changing the number of photos changes weight and price, and an
  // already-computed panel would go on showing the previous variant's quote. It
  // mirrors variant A's bridge (assets/pdp-configurator.js).
  function syncShippingVariant() {
    const host = document.getElementById('pdpAplPrazoHost');
    const island = host?.querySelector('[data-prazo-config]');
    if (!island) return;
    const variant = cfg.variants.find((candidate) => candidate.id === state.fotosVariantId);
    if (!variant) return;
    try {
      const prazoCfg = JSON.parse(island.textContent);
      prazoCfg.variant = { sku: variant.sku, price: variant.price, weightGrams: variant.weightGrams };
      island.textContent = JSON.stringify(prazoCfg);
    } catch {
      return; // unreadable island: better to keep the previous quote than clear it
    }
    const panel = host.querySelector('[data-prazo-panel]');
    if (panel) panel.hidden = true;
  }

  // ── summary / total ──
  // The recap reads the engraving back as typed, and it is typed in whatever case
  // the customer feels like — the album engraves uppercase either way. Lowercase in
  // a column where Álbum, Cor, Gravação and Proteção all arrive capitalised read as
  // a slip (20/08 review). Only the initials are raised, so an all-caps entry stays
  // as written. Display only: the cart property and the JSON blob still carry the
  // text exactly as the customer typed it.
  const raiseInitials = (text) =>
    text.replace(/(^|\s)(\p{L})/gu, (whole, lead, initial) => lead + initial.toLocaleUpperCase('pt-BR'));

  // ── photo upsell (scrapbooks) ──
  // Sold instead of the music add-on, and sold per size: 5x6 and 9x12 can both be in
  // the order, each with its own quantity, each reaching the cart as its own line.
  // Prices, variant ids and the discount ladder come from the published Fotos
  // products through snippets/pdp-photo-addon-data.liquid, never from here — the
  // price on screen has to be the price checkout charges.
  const photoAddon = cfg.photoAddon || null;
  const photoStrings = strings.photo || {};
  const photoSection = $('#pdpAplSecPhotos');
  const photoSizesWrap = $('#pdpAplPhotoSizes');
  const photoTabsWrap = $('#pdpAplPhotoTabs');
  const photoStatus = $('#pdpAplPhotoStatus');

  const photoSizeById = (sizeId) => (photoAddon ? photoAddon.sizes.find((size) => size.id === sizeId) : null);
  const photoUnitPrice = (size) => (size.prices && size.prices[state.photoTab]) || 0;
  // Plural, for the summary line; the tabs carry the singular they need as buttons.
  const photoFinishLabel = () => (photoStrings.finishes && photoStrings.finishes[state.photoTab]) || '';

  // The field's maxlength caps what can be TYPED; this caps what the stepper and the
  // package pills commit, and it runs once more at add-to-cart. The minimum is applied
  // last and wins: it is a product rule, while the ceiling is an operational setting,
  // so a low ceiling must not push a quantity under the rule. Floored because the
  // setting accepts decimals and a fractional quantity is rejected by the cart.
  const clampPhotoQty = (quantity) => {
    if (!photoAddon) return 0;
    const ceiling = Math.min(9999, Number(photoAddon.maxQty) || 9999);
    const wanted = Number(quantity) || photoAddon.minQty;
    return Math.max(photoAddon.minQty, Math.floor(Math.min(ceiling, wanted)));
  };

  const photoTierFor = (quantity) => {
    if (!photoAddon) return null;
    return (photoAddon.tiers || []).reduce(
      (best, tier) => (quantity >= tier.q && (!best || tier.q > best.q) ? tier : best),
      null,
    );
  };

  const photoLineTotal = (size, quantity) => {
    const tier = photoTierFor(quantity);
    return Math.round(photoUnitPrice(size) * (1 - (tier ? tier.p : 0) / 100)) * quantity;
  };

  const photoTotal = () => {
    if (!photoAddon) return 0;
    return photoAddon.sizes.reduce((sum, size) => {
      const quantity = state.photoQtys[size.id];
      return quantity == null ? sum : sum + photoLineTotal(size, quantity);
    }, 0);
  };

  // The quantity the shopper is looking at, which is the draft whenever one exists:
  // WebKit does not blur a focused field when a button is tapped, so an emptied field
  // never reaches photoQtys and the committed value alone would be stale.
  const photoShownQty = (sizeId) => {
    const draft = state.photoDrafts[sizeId];
    return draft === undefined ? state.photoQtys[sizeId] : parseInt(draft, 10);
  };

  const announcePhotoQty = (size, quantity) => {
    if (!photoStatus) return;
    photoStatus.textContent = `${photoStrings.qtyLabel || 'Quantidade'} — ${size.label}: ${quantity}`;
  };

  function renderPhotoPacks(packsWrap, quantity) {
    packsWrap.textContent = '';
    (photoAddon.tiers || []).forEach((tier) => {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.dataset.photoPack = String(tier.q);
      pill.className = quantity === tier.q ? 'on' : '';
      const quantityLabel = document.createElement('b');
      quantityLabel.textContent = `${tier.q} un`;
      const offLabel = document.createElement('span');
      offLabel.textContent = `${tier.p}${photoStrings.offSuffix || '% OFF'}`;
      pill.append(quantityLabel, offLabel);
      packsWrap.appendChild(pill);
    });
  }

  function renderPhotoAddon() {
    if (!photoAddon || !photoSizesWrap) return;
    // Reopened from the drawer, the page is editing ONE album group. Photos are lines
    // of their own, outside that group, so an edit has nothing to say about them —
    // and an upsell left open here would append a second photos line on every save.
    // The control arm withholds it in the edit panel for the same reason.
    if (editKey) {
      if (photoSection) photoSection.hidden = true;
      return;
    }
    $$('[data-photo-tab]', photoTabsWrap).forEach((tab) => {
      const isSelected = tab.dataset.photoTab === state.photoTab;
      tab.classList.toggle('on', isSelected);
      tab.setAttribute('aria-pressed', String(isSelected));
    });

    $$('[data-photo-size]', photoSizesWrap).forEach((card) => {
      const size = photoSizeById(card.dataset.photoSize);
      if (!size) return;
      const quantity = state.photoQtys[size.id];
      const isOpen = quantity != null;

      card.querySelector('[data-photo-toggle]').classList.toggle('on', isOpen);

      const unitEl = card.querySelector('[data-photo-unit]');
      unitEl.textContent = `${money(photoUnitPrice(size))}${photoStrings.unitSuffix || '/un'}`;

      const panel = card.querySelector('[data-photo-panel]');
      panel.hidden = !isOpen;
      if (!isOpen) return;

      renderPhotoPacks(card.querySelector('[data-photo-packs]'), quantity);

      // Closes the panel. Per size AND per finish, so it follows the tab; 0 means the
      // legacy store had no package for that combination and nothing is claimed.
      const suggestion = (size.suggestion && size.suggestion[state.photoTab]) || 0;
      const suggEl = card.querySelector('[data-photo-sugg]');
      suggEl.hidden = suggestion === 0;
      suggEl.textContent = suggestion
        ? (photoStrings.suggestion || 'Sugestão para completar o álbum: {n} fotos').replace('{n}', suggestion)
        : '';

      // Leave the field alone while a draft is live — it already shows the keystrokes,
      // and writing over them would fight the shopper mid-number.
      const qtyInput = card.querySelector('[data-photo-qty]');
      if (state.photoDrafts[size.id] === undefined) qtyInput.value = String(quantity);

      // What this size costs, and under it whether a package discount is in play —
      // the multiplication that used to sit there restated the two numbers beside it.
      const tier = photoTierFor(quantity);
      card.querySelector('[data-photo-value]').textContent = money(photoLineTotal(size, quantity));
      card.querySelector('[data-photo-discount]').textContent = tier
        ? photoStrings.discountApplied || 'Desconto aplicado'
        : '';
    });
  }

  const setPhotoQty = (sizeId, quantity) => {
    if (quantity == null) {
      delete state.photoQtys[sizeId];
    } else {
      state.photoQtys[sizeId] = quantity;
    }
    // A size that leaves the order takes its draft with it: reopening it later has to
    // start from the fresh quantity, not from what was typed before it left.
    delete state.photoDrafts[sizeId];
    renderPhotoAddon();
    refresh();
  };

  photoTabsWrap?.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-photo-tab]');
    if (!tab) return;
    state.photoTab = tab.dataset.photoTab;
    renderPhotoAddon();
    refresh();
  });

  photoSizesWrap?.addEventListener('click', (event) => {
    const card = event.target.closest('[data-photo-size]');
    if (!card) return;
    const size = photoSizeById(card.dataset.photoSize);
    if (!size) return;

    if (event.target.closest('[data-photo-toggle]')) {
      setPhotoQty(size.id, state.photoQtys[size.id] == null ? clampPhotoQty(photoAddon.featuredQty) : null);
      return;
    }
    if (event.target.closest('[data-photo-remove]')) {
      setPhotoQty(size.id, null);
      return;
    }
    const pack = event.target.closest('[data-photo-pack]');
    if (pack) {
      setPhotoQty(size.id, clampPhotoQty(Number(pack.dataset.photoPack)));
      return;
    }
    const stepper = event.target.closest('[data-photo-step]');
    if (stepper) {
      // Step from what the field shows, and clamp the base before stepping it: a raw
      // typed 5 has to become the minimum first, or "+" commits 6 and reads as dead.
      const base = clampPhotoQty(photoShownQty(size.id));
      const stepped = clampPhotoQty(base + Number(stepper.dataset.photoStep));
      setPhotoQty(size.id, stepped);
      announcePhotoQty(size, stepped);
    }
  });

  photoSizesWrap?.addEventListener('input', (event) => {
    const qtyInput = event.target.closest('[data-photo-qty]');
    if (!qtyInput) return;
    const card = qtyInput.closest('[data-photo-size]');
    const digits = qtyInput.value.replace(/\D/g, '');
    qtyInput.value = digits;
    state.photoDrafts[card.dataset.photoSize] = digits;
    const typed = parseInt(digits, 10);
    // Commit only real numbers; the minimum waits for blur, so typing "100" is not
    // rewritten into the minimum the moment the "1" lands.
    if (typed > 0) {
      state.photoQtys[card.dataset.photoSize] = typed;
      renderPhotoAddon();
      refresh();
    }
  });

  photoSizesWrap?.addEventListener(
    'blur',
    (event) => {
      const qtyInput = event.target.closest('[data-photo-qty]');
      if (!qtyInput) return;
      const size = photoSizeById(qtyInput.closest('[data-photo-size]').dataset.photoSize);
      const typed = parseInt(qtyInput.value, 10);
      const clamped = clampPhotoQty(typed);
      setPhotoQty(size.id, clamped);
      // Announce only when the clamp CHANGED the number — an unchanged one was already
      // echoed by the screen reader while it was typed.
      if (clamped !== typed) announcePhotoQty(size, clamped);
    },
    true,
  );

  photoSizesWrap?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.closest('[data-photo-qty]')) return;
    event.preventDefault();
    event.target.blur();
  });

  function refresh() {
    const fotosVariant = cfg.variants.find((v) => v.id === state.fotosVariantId);
    // The extra line only charges when there is actual text: an open, empty field
    // would reach the cart as linha2 = null with money added on top.
    const lineAddonCents = state.mode === 'now' && state.t2 && state.t2.trim() ? cfg.linePrice || 0 : 0;
    const musicCents = state.music === 1 ? cfg.musicPrice || 0 : 0;
    const total = state.fotosPrice + state.protPrice + musicCents + lineAddonCents + photoTotal();

    if (orderBox) {
      orderBox.innerHTML = '';
      const row = (l, v) => {
        const d = document.createElement('div');
        const l1 = document.createElement('span');
        l1.textContent = l;
        const l2 = document.createElement('span');
        l2.textContent = v;
        d.append(l1, l2);
        orderBox.appendChild(d);
      };
      row('Álbum', `${cfg.product.title} · ${cfg.product.sizeLabel || ''}`.trim());
      // Only where the photo count was hers to choose. On a single-variant product the
      // rail hides that question, and the variant title is the format ("A4") rather
      // than a number of photos — as a "Fotos" row it reads as an answer she never gave.
      if (fotosVariant && cfg.variants.length > 1) row('Fotos', fotosVariant.title);
      if (state.mode === 'now') {
        row('Cor', cfg.colors[state.color]?.label || '');
        row('Gravação', state.foil === 'dourado' ? 'Dourado' : 'Prata');
        if (state.t1.trim()) row('Texto da capa', raiseInitials(state.t1.trim()));
        if (state.t2 !== null && state.t2.trim()) row('Segunda linha', raiseInitials(state.t2.trim()));
        if (hasSideText && state.side.trim()) row('Texto da lateral', raiseInitials(state.side.trim()));
      } else {
        row('Cor e gravação', 'Você escolhe depois');
      }
      row('Proteção', state.protId === 'none' ? 'Sem proteção' : state.protId === 'luva' ? 'Caixa Luva' : 'Caixa Cristal');
      // Scrapbooks bought photos where every other album is asked about music, so the
      // summary asks the same trade — one row per size she chose, and no music row for
      // a question this product never put to her.
      if (photoAddon) {
        photoAddon.sizes.forEach((size) => {
          const quantity = state.photoQtys[size.id];
          if (quantity == null) return;
          const tier = photoTierFor(quantity);
          // The finish is half of what she is buying — 5x6 autocolante and 5x6
          // tradicional are different products at different prices, and the summary
          // was naming only the size.
          row(`Fotos ${photoFinishLabel()} ${size.label}`, `${quantity} un${tier ? ` · ${tier.p}% off` : ''}`);
        });
      } else {
        row('Música', state.music === 1 ? (state.track ? `${state.track.name} · ${state.track.artist}` : 'Com música') : 'Sem música');
      }
    }

    if (priceBig) priceBig.textContent = money(total);
    const { count, perInstallmentCents } = resolveInstallments(total, cfg.installmentsRule);
    if (priceInst) priceInst.textContent = count > 1 ? `em até ${count}x de ${money(perInstallmentCents)} sem juros` : '';
    if (pricePix) pricePix.textContent = `${money(Math.round(total * (1 - (cfg.pixPercent || 0) / 100)))} (${cfg.pixPercent}% de desconto via Pix)`;
    if (barTotal) barTotal.textContent = money(total);
    if (cta) cta.disabled = adding || hydrating || !fotosVariant || !fotosVariant.available;

    updateMini();

    const parts = [cfg.product.sizeLabel || cfg.product.title];
    if (fotosVariant && cfg.variants.length > 1) parts.push(fotosVariant.title);
    parts.push(state.mode === 'now' ? cfg.colors[state.color]?.label || '' : 'personalização depois');
    if (state.protId !== 'none') parts.push(state.protId === 'luva' ? 'Caixa Luva' : 'Caixa Cristal');
    if (state.music === 1) parts.push('música');
    if (photoAddon) {
      photoAddon.sizes.forEach((size) => {
        const quantity = state.photoQtys[size.id];
        if (quantity != null) parts.push(`${quantity} fotos ${size.id}`);
      });
    }
    if (barSub) barSub.textContent = parts.filter(Boolean).join(' · ');

    // Last, and on every change: the render follows the album, and the module
    // debounces, so asking on each edit costs one upload for a burst of typing.
    schedulePreviewCapture();
  }

  // ── the image the cart shows ──
  // The drawer shows the album as she configured it, not the variant's catalogue
  // shot, and that image is a render this arm has to produce and upload: the drawer
  // reads the `_preview_image` property, and only the reference was writing it, so a
  // line bought through this arm reached the cart with no image at all.
  //
  // Rendered by the same module the reference uses, from the same geometry — both
  // imported, neither copied. Loaded lazily because nothing here is needed until she
  // personalises, and with no endpoint (a dev store) the capture stays inert and
  // add-to-cart is untouched.
  const INERT_CAPTURE = { schedule() {}, clear() {}, current: () => null, settle: async () => null };
  let previewCapture = INERT_CAPTURE;
  let previewFs = {};
  let captureArming = null;

  function armPreviewCapture() {
    if (captureArming) return captureArming;
    const endpoint = cfg.routes?.previewUpload || '';
    if (!endpoint) {
      captureArming = Promise.resolve();
      return captureArming;
    }
    captureArming = Promise.all([import('./pdp-preview-capture.js'), import('./pdp-preview-geometry.js')])
      .then(([capture, geometry]) => {
        const group = geometry.PREVIEW_GROUP_BY_SLUG[cfg.product.handle] || 'classic';
        previewFs = geometry.PREVIEW_FS[cfg.product.handle] || geometry.PREVIEW_FS[geometry.PREVIEW_FS_FALLBACK[group]] || {};
        previewCapture = capture.createPreviewCapture({
          endpoint,
          geo: geometry.PREVIEW_POS[group],
          iconMap: Object.fromEntries((cfg.icons || []).map((icon) => [icon.name, icon.url])),
        });
      })
      .catch(() => {
        // A render that fails to load must not cost her the purchase.
        previewCapture = INERT_CAPTURE;
      });
    return captureArming;
  }

  // The font sizes are the reference's cqw values, not this arm's cqh ones: the
  // canvas is the reference's frame, so it has to be measured in the reference's
  // units. Same reason the anchors come from there.
  const captureCoverFs = (size) => previewFs['cover' + (size || 'P').toUpperCase()] || previewFs.coverM || previewFs.coverP || 4;
  const captureSpineFs = (size) => previewFs['spine' + (size || 'P').toUpperCase()] || previewFs.spineP || previewFs.spineM || 3;

  function schedulePreviewCapture() {
    if (state.mode !== 'now') {
      previewCapture.clear();
      return;
    }
    armPreviewCapture().then(() => {
      if (state.mode !== 'now') return;
      previewCapture.schedule({
        imageSrc: cfg.colorImages[state.color] || cfg.colorImages[0] || '',
        colorLabel: cfg.colors[state.color]?.label || '',
        foilId: state.foil || 'prata',
        coverLines: engravedCoverLines(),
        sideText: hasSideText ? state.side.trim() : '',
        sideIcon: '',
        coverFontCqw: captureCoverFs(state.size),
        spineFontCqw: captureSpineFs(state.sideSize),
      });
    });
  }

  // ── cart ──
  // The engraved cover lines in order, empty ones dropped — the shape the reference
  // uses both for the cart's personalisation blob and for the image the drawer shows.
  // Two readers, one definition: a line that reaches the cart and a line that reaches
  // the render have to be the same line.
  const engravedCoverLines = () =>
    [state.t1, state.t2]
      .map((line) => ({ text: (line || '').trim(), icon: '' }))
      .filter((line) => line.text.length);

  function buildItems(group) {
    const items = [];
    const props = {};
    if (state.mode === 'now') {
      const color = cfg.colors[state.color];
      props['Cor da capa'] = color?.label;
      props['Cor da gravação'] = state.foil === 'dourado' ? 'Dourado' : 'Prata';
      if (state.t1.trim()) {
        props['Texto da capa'] = state.t1.trim();
        if (cfg.hasCoverSizes) props['Tamanho do título'] = state.size;
      }
      // Visible, not only inside _personalizacao: the drawer's "Linha extra"
      // chip carries no content, so this is where the customer re-reads line
      // two (QA 24/08). Same label the recap uses; the drawer's catch-all
      // property loop renders it.
      if (state.t2 && state.t2.trim()) props['Segunda linha'] = state.t2.trim();
      if (hasSideText && state.side.trim()) {
        props['Texto da lateral'] = state.side.trim();
        // The name the reference writes, so the warehouse and the drawer edit
        // surface read one vocabulary. Nested under the text like the cover size
        // is: a size with no text to engrave says nothing.
        if (cfg.hasSideSizes) props['Tamanho da lateral'] = state.sideSize;
      }
      // Hidden: the preview image of the chosen colour, resolved the way the tested
      // configurator does it (pdp-configurator.js) — the drawer shows that colour in
      // the thumbnail instead of the variant's featured image.
      const coverImage = cfg.colorImages[state.color] || cfg.colorImages[0] || '';
      if (coverImage) props['_cover_image'] = coverImage;
      props['_cover_hex'] = color?.hex;
      // The album as she configured it. Same property the reference writes, because
      // it is the one the drawer reads; absent when the render never landed, and the
      // drawer falls back to the cover shot above.
      const previewUrl = previewCapture.current();
      if (previewUrl) props['_preview_image'] = previewUrl;
      // The shape is the reference's, field for field — see buildProperties in
      // pdp-configurator.js. It has to be: the cart drawer picks its editor from the
      // product's template suffix, which says nothing about which arm the order came
      // through, so a line bought here is reopened by the reference's editor. Written
      // in this arm's own shape, that editor found none of the fields it reads and
      // the customer could not edit what she had just bought.
      props['_personalizacao'] = JSON.stringify({
        cor: color ? { label: color.label, hex: color.hex } : null,
        gravacao: state.foil || null,
        titulo: {
          tamanho: state.size,
          // One entry per engraved line, in order, the way the reference stores them:
          // this arm's second line is simply the second entry.
          linhas: engravedCoverLines().map((line) => ({ texto: line.text, simbolo: line.icon || null })),
        },
        lateral: {
          texto: hasSideText ? state.side.trim() : '',
          tamanho: hasSideText && cfg.hasSideSizes ? state.sideSize : null,
          simbolo: null,
        },
      });
    }
    props['_group'] = group;
    items.push({ id: state.fotosVariantId, quantity: 1, properties: props });

    if (state.protVariantId) {
      const addonKey = state.protId === 'luva' ? 'caixa_luva' : 'caixa_cristal';
      items.push({ id: state.protVariantId, quantity: 1, properties: { _group: group, _addon: addonKey } });
    }
    if (state.music === 1 && cfg.musicVariantId) {
      const musicProps = { _group: group, _addon: 'musica' };
      if (state.track) {
        // " — " is the separator cart-edit restores (the split lives in
        // pdp-configurator.js); " · " would break the parsing of the artist.
        musicProps['Música'] = state.track.artist ? `${state.track.name} — ${state.track.artist}` : state.track.name;
        // Name and artist as their own hidden fields, so hydration reads them
        // back without splitting the label — a title containing " — " would
        // come back with the fields swapped otherwise. The label stays the
        // shared display contract with the classic arm.
        musicProps['_spotify_name'] = state.track.name || '';
        if (state.track.artist) musicProps['_spotify_artist'] = state.track.artist;
        musicProps['_spotify_image'] = state.track.art;
        musicProps['_spotify_url'] = state.track.url || '';
      }
      items.push({ id: cfg.musicVariantId, quantity: 1, properties: musicProps });
    }
    if (state.mode === 'now' && state.t2 && state.t2.trim() && cfg.lineVariantId) {
      items.push({ id: cfg.lineVariantId, quantity: 1, properties: { _group: group, _addon: 'linha' } });
    }
    // Each chosen photo size is an INDEPENDENT Fotos line — its own fresh _group and no
    // _addon — rather than an add-on nested under the album, so editing the album never
    // touches it. Field for field the control arm's contract (buildProperties in
    // pdp-configurator.js): the drawer and the order have to read both arms alike.
    if (photoAddon && !editKey) {
      photoAddon.sizes.forEach((size) => {
        const shown = photoShownQty(size.id);
        const variantId = size.variantIds && size.variantIds[state.photoTab];
        // A missing Fotos variant skips the line and leaves the rest of the order
        // intact — the same no-op the control arm falls back to.
        if (shown == null || !variantId) return;
        // Clamp once more at the cart boundary: WebKit does not blur a focused field
        // when a button is tapped, so a package or stepper click can commit past the
        // minimum or the ceiling while the field never revalidates, and an emptied
        // draft parses to NaN. The correction has to reach the screen as well — the
        // totals read photoQtys, so leaving the raw value there would charge for one
        // number while the page still shows another.
        const quantity = clampPhotoQty(shown);
        if (quantity !== state.photoQtys[size.id] || state.photoDrafts[size.id] !== undefined) {
          setPhotoQty(size.id, quantity);
        }
        items.push({
          id: variantId,
          quantity,
          properties: { _group: newGroupId(), 'Tamanho': `${size.id}cm`, 'Quantidade': String(quantity) },
        });
      });
    }
    return items;
  }

  let adding = false;
  const fallbackAddError = strings.addError || 'Não foi possível adicionar ao carrinho. Tente novamente.';
  const showAddError = (msg) => {
    if (!addErrorEl) return;
    addErrorEl.textContent = msg;
    addErrorEl.hidden = false;
  };

  // ── edit mode (?edit=<line_key>) ──
  // The drawer's "Editar personalização" link has always pointed here; until the
  // 24/08 QA nothing read the parameter and the page opened blank — "personalize
  // later", Off White, empty fields — and the customer rebuilt everything. A key
  // the cart no longer holds leaves the page as an ordinary buy box, no error.
  const editKey = new URLSearchParams(window.location.search).get('edit');
  let editKeys = null;
  let pendingSave = null;
  // While hydrateEdit is still reading the bag, editKeys is null and a click
  // on the CTA would run a plain add — duplicating the very group the edit is
  // about to replace. The gate holds the CTA until hydration settles, either
  // way it settles.
  let hydrating = !!editKey;
  const editCfg = { routes: cfg.routes, strings };

  // The DOM half of edit hydration: readAlbumEditState (module scope, tested)
  // decides WHAT the line holds; this applies it through the same paths the
  // handlers use.
  function applyEditState(saved) {
    editKeys = saved.keys;
    const variant = cfg.variants.find((candidate) => candidate.id === saved.variantId);
    if (variant) {
      state.fotosVariantId = variant.id;
      state.fotosPrice = variant.price;
      const variantBtn = $$('.opt', optFotos).find((el) => Number(el.dataset.variantId) === variant.id);
      if (variantBtn) pick(optFotos, variantBtn);
      syncShippingVariant();
    }
    const personalization = saved.personalization;
    if (personalization) {
      state.mode = 'now';
      const nowBtn = $$('.opt', optModo).find((el) => el.dataset.m === 'now');
      if (nowBtn) pick(optModo, nowBtn);
      revealSection(secCor, true);
      revealSection(secGrav, true);
      stage?.classList.add('custom');
      media?.classList.add('custom');
      preview?.classList.add('on');
      warmColorImages();
      if (personalization.foil) {
        state.foil = personalization.foil;
        $$('button', foilSeg).forEach((el) => el.classList.toggle('on', el.dataset.foil === state.foil));
        const gold = state.foil === 'dourado';
        [pvT1, pvT2, pvSide].forEach((el) => {
          if (!el) return;
          el.classList.toggle('c-gold', gold);
          el.classList.toggle('c-silver', !gold);
        });
      }
      if (personalization.titleSize && titleSizes.includes(personalization.titleSize)) {
        state.size = personalization.titleSize;
        $$('button', sizesWrap).forEach((el) => el.classList.toggle('on', el.dataset.s === state.size));
        [pvT1, pvT2].forEach((el) => {
          el?.classList.toggle('size-m', state.size === 'M');
          el?.classList.toggle('size-g', state.size === 'G');
        });
      }
      state.t1 = sanitizeText(personalization.line1);
      if (t1Input) t1Input.value = state.t1;
      if (personalization.line2) {
        state.t2 = sanitizeText(personalization.line2);
        if (fieldT2) fieldT2.classList.add('open');
        if (addLine) addLine.textContent = `− ${strings.removeLine || 'Remover linha'} (− ${money(cfg.linePrice || 0)})`;
        if (t2Input) t2Input.value = state.t2;
      }
      // Re-trims to the restored size's limit and redraws counters + preview —
      // the same path a size click takes.
      applyCoverLimit();
      if (hasSideText) {
        if (personalization.sideSize && spineSizes.includes(personalization.sideSize)) {
          state.sideSize = personalization.sideSize;
          $$('button', sideSizesWrap).forEach((el) => el.classList.toggle('on', el.dataset.s === state.sideSize));
        }
        state.side = sanitizeText(personalization.sideText);
        if (sideInput) sideInput.value = state.side;
        applySpineSize();
      }
      let colorIdx = cfg.colors.findIndex((colorOption) => colorOption.label === personalization.colorLabel);
      if (colorIdx < 0) colorIdx = cfg.colors.findIndex((colorOption) => colorOption.hex === personalization.colorHex);
      // selectColor already pulls the render + refresh; a colour that left the
      // palette keeps the default swatch, render loaded all the same.
      selectColor(colorIdx >= 0 ? colorIdx : state.color);
    }
    if (saved.protection) {
      const protBtn = $$('.opt', optProt).find((el) => el.dataset.protId === saved.protection);
      if (protBtn) {
        state.protId = saved.protection;
        state.protPrice = Number(protBtn.dataset.p);
        state.protVariantId = protBtn.dataset.variantId ? Number(protBtn.dataset.variantId) : null;
        pick(optProt, protBtn);
      }
    }
    if (saved.musicOn) {
      const musicBtn = $$('.opt', optMusic).find((el) => el.dataset.mu === '1');
      if (musicBtn) {
        state.music = 1;
        pick(optMusic, musicBtn);
        musicPicker?.classList.add('open');
      }
      if (saved.track) chooseTrack(saved.track);
    }
    if (cta && strings.saveChanges) cta.textContent = strings.saveChanges;
    refresh();
  }

  async function hydrateEdit() {
    if (!editKey || !cfg.routes.cart) {
      hydrating = false;
      refresh();
      return;
    }
    try {
      const res = await fetch(cfg.routes.cart, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const saved = readAlbumEditState(cart, editKey);
      // A broken blob walks away from edit mode entirely: editKeys stays null,
      // so nothing can replace the personalization this page failed to read.
      if (!saved || saved.blobBroken) return;
      applyEditState(saved);
    } catch {
      // graceful fallback: ordinary buy box
    } finally {
      hydrating = false;
      refresh();
    }
  }

  cta?.addEventListener('click', async () => {
    const variant = cfg.variants.find((v) => v.id === state.fotosVariantId);
    if (adding || hydrating || !variant || !variant.available) return;
    if (addErrorEl) {
      addErrorEl.hidden = true;
      addErrorEl.textContent = '';
    }
    adding = true;
    cta.disabled = true;
    try {
      // The render is debounced while she types, so at this point the last edit may
      // still be in flight. Wait for it, bounded — an upload that stalls costs the
      // image, never the purchase — and only then read the url into the line.
      if (state.mode === 'now') await previewCapture.settle(4000);
      if (editKeys) {
        // In edit mode the whole group is replaced (add first, atomic removal
        // after) by the extracted engine — never a second line.
        const sections = await saveGroup({
          config: editCfg,
          state: {},
          editKey,
          fallbackKeys: editKeys,
          sectionsUrl: window.location.pathname,
          pending: pendingSave,
          itemsFor: (group) => buildItems(group),
        });
        pendingSave = null;
        document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections } }));
        return;
      }
      const res = await fetch(cfg.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: buildItems(newGroupId()), sections: 'cart-drawer', sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok) {
        showAddError((data && (data.description || data.message)) || fallbackAddError);
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections: data.sections } }));
      // Its own event, because `cart:open` also rides the edit-save path above:
      // listening to that one would count an edited line as a PDP add. Mirrors
      // the control configurator so `pdp_resultado` means the same on both arms.
      document.dispatchEvent(new CustomEvent('fn:pdp-added'));
    } catch (error) {
      // A half-finished save records what it already did (pending) so a retry
      // finishes the removal instead of adding a third copy; sections riding on
      // the error re-render the bag so the old copy the message names is visible.
      if (error && error.pending) {
        pendingSave = error.pending;
        if (error.pending.sections) {
          document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections: error.pending.sections } }));
        }
      }
      showAddError((error && error.message) || fallbackAddError);
    } finally {
      adding = false;
      refresh(); // re-enables the CTA only if the selected variant is still available
    }
  });

  // Publishes the staybar's height so the CSS can push the WhatsApp float away from
  // the CTA (it grows when the error appears; a ResizeObserver keeps up).
  const staybarEl = root.querySelector('.staybar');
  if (staybarEl && 'ResizeObserver' in window) {
    new ResizeObserver(() => {
      document.documentElement.style.setProperty('--pdp-staybar-h', `${staybarEl.offsetHeight}px`);
    }).observe(staybarEl);
  }

  updateHeader();
  // Before the first refresh: the per-unit prices and the suggestions are the only
  // part of the upsell Liquid cannot render, and an empty price is what she would see
  // between paint and this call.
  renderPhotoAddon();
  refresh();
  hydrateEdit();
}
